import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, AutocompleteItem, Chip, Select, SelectItem, Spinner, Textarea } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { AdminApiError, useAdminClient, type AdminAgent, type AdminUser, type CheckResult } from './api';
import { COLUMN_ACTIONS, ErrorLine, SCOPE_LABELS, TabIntro, columnAction, useAdminLoad } from './shared';

/** What each query rejection code means, for an admin (the API's message follows it). */
const REJECTIONS: Record<string, string> = {
  parse_error: 'The SQL could not be parsed.',
  multiple_statements: 'Only one statement can be checked at a time.',
  not_a_select: 'Only SELECT queries are allowed.',
  forbidden_construct: 'The query uses a construct that is not allowed.',
  forbidden_function: 'The query calls a function that is not allowed.',
  three_part_name: 'Datasets must be named as the catalog names them, not database.schema.table.',
  column_not_available: 'The query uses a column this user cannot see through this agent.',
  unknown_column: 'The query names a column the dataset does not have.',
  cross_database: 'The query reads from more than one database.',
  no_dataset: 'The query reads no catalog dataset.',
  bad_metric_params: 'The metric parameters are not valid.',
  bad_filter: 'A filter is not valid.',
  filter_not_allowed: 'A filter uses a column this user does not see as it is.',
};

interface Rejection {
  title: string;
  message: string;
  code: string | null;
}

/**
 * Check access: what a user may see through an agent, and how a query of theirs would be
 * rewritten. The service works it out from grants and the catalog; nothing runs against the warehouse.
 */
export function CheckAccessTab({ baseUrl }: { readonly baseUrl: string }) {
  const { t } = useTranslation();
  const client = useAdminClient(baseUrl);
  const { data, loading, error } = useAdminLoad<{ users: AdminUser[]; agents: AdminAgent[] }>(client, async (c, signal) => {
    const [users, agents] = await Promise.all([c.users(signal), c.agents(signal)]);
    return { users, agents };
  });
  const [userId, setUserId] = useState<string | null>(null);
  const [agentId, setAgentId] = useState<string | null>(null);
  // External assistants (the first agent) until the admin picks another.
  const firstAgent = data?.agents?.[0]?.id ?? null;
  useEffect(() => {
    if (firstAgent) setAgentId((current) => current ?? firstAgent);
  }, [firstAgent]);
  const [sql, setSql] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [rejection, setRejection] = useState<Rejection | null>(null);

  const check = async () => {
    if (!client || !userId || !agentId) return;
    setChecking(true);
    setRejection(null);
    try {
      setResult(await client.check({ user_id: userId, agent: agentId, sql: sql.trim() || null, filters: null }));
    } catch (e) {
      setResult(null);
      const err = e as AdminApiError;
      const isRejection = err instanceof AdminApiError && err.status === 400 && Boolean(err.code);
      setRejection({
        title: isRejection ? t(REJECTIONS[err.code ?? ''] ?? 'The query would be refused.') : t('The check failed.'),
        message: err.message,
        code: isRejection ? err.code : null,
      });
    } finally {
      setChecking(false);
    }
  };

  const users = data?.users ?? [];
  const agents = data?.agents ?? [];
  const agentLabel = (id: string) => agents.find((agent) => agent.id === id)?.label ?? id;

  return (
    <div className="flex flex-col gap-4 px-6 pb-6">
      <TabIntro title={t('Check access')}>
        <span className="text-sm text-default-500">
          {t('See what a user may read through an agent, and how a query of theirs would be rewritten.')}
        </span>
        <span className="text-xs text-default-400">
          {t('Nothing runs against the warehouse: the check only works out access from the grants and the catalog.')}
        </span>
      </TabIntro>

      {loading && <Spinner size="sm" className="self-start" />}
      <ErrorLine message={error} />

      <div className="flex flex-col gap-3 lg:max-w-3xl">
        <div className="flex flex-col gap-3 sm:flex-row">
          <Autocomplete
            label={t('User')}
            placeholder={t('Search users')}
            size="sm"
            className="sm:max-w-xs"
            defaultItems={users}
            selectedKey={userId}
            onSelectionChange={(key) => setUserId(key === null || key === undefined ? null : String(key))}
            isDisabled={!data}
          >
            {(user) => (
              <AutocompleteItem key={user.user_id} textValue={user.name || user.email || user.user_id}>
                <div className="flex flex-col">
                  <span className="text-sm">{user.name || user.email || user.user_id}</span>
                  {user.email ? <span className="text-xs text-default-500">{user.email}</span> : null}
                </div>
              </AutocompleteItem>
            )}
          </Autocomplete>
          <Select
            label={t('Agent')}
            size="sm"
            className="sm:max-w-xs"
            selectedKeys={agentId ? [agentId] : []}
            disallowEmptySelection
            onSelectionChange={(keys) => {
              if (keys === 'all') return;
              const key = Array.from(keys)[0];
              setAgentId(key === undefined ? null : String(key));
            }}
            isDisabled={!data}
          >
            {agents.map((agent) => (
              <SelectItem key={agent.id} value={agent.id} textValue={agent.label}>{agent.label}</SelectItem>
            ))}
          </Select>
        </div>
        <Textarea
          label={t('SQL (optional)')}
          placeholder="SELECT …"
          minRows={3}
          value={sql}
          onValueChange={setSql}
          classNames={{ input: 'font-mono text-xs' }}
        />
        <CtyButton
          color="primary"
          size="sm"
          className="self-start"
          onPress={() => void check()}
          isDisabled={!userId || !agentId || checking}
          isLoading={checking}
        >
          {t('Check')}
        </CtyButton>
      </div>

      {rejection && (
        <div role="alert" className="flex flex-col gap-1 rounded-medium border border-danger-200 bg-danger-50 p-3 text-sm">
          <span className="flex items-center gap-2 font-semibold text-danger">
            <Icon icon="material-symbols:error-outline" width={18} /> {rejection.title}
          </span>
          {rejection.message ? <span className="text-foreground">{rejection.message}</span> : null}
          {rejection.code ? <span className="font-mono text-xs text-default-500">{rejection.code}</span> : null}
        </div>
      )}

      {result && <CheckResultView result={result} agentLabel={agentLabel(result.agent?.id ?? '')} />}
    </div>
  );
}

function CheckResultView({ result, agentLabel }: { readonly result: CheckResult; readonly agentLabel: string }) {
  const { t } = useTranslation();
  const datasets = Object.entries(result.datasets ?? {});
  const who = result.user?.name || result.user?.email || result.user?.user_id;
  return (
    <section aria-label={t('Check result')} className="flex flex-col gap-3 rounded-medium border border-default-200 p-3">
      <span className="text-sm text-default-500">
        {t('{{user}} through {{agent}}', { user: who, agent: agentLabel })}
        {result.agent?.kind ? ` (${result.agent.kind})` : ''}
      </span>
      {result.identity_note && <span className="text-xs text-default-400">{result.identity_note}</span>}
      {datasets.length === 0 ? (
        <p className="text-sm text-default-500">{t('This user can read no datasets through this agent.')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-default-500">
              <tr>
                <th className="py-1 pr-4 font-medium">{t('Dataset')}</th>
                <th className="py-1 pr-4 font-medium">{t('Row scope')}</th>
                <th className="py-1 pr-4 font-medium">{t('Visible columns')}</th>
                <th className="py-1 font-medium">{t('Hidden columns')}</th>
              </tr>
            </thead>
            <tbody>
              {datasets.flatMap(([name, dataset]) => {
                const rows = [(
                  <tr key={name} className="border-t border-default-100 align-top" aria-label={name}>
                    <td className="py-1 pr-4 font-mono text-xs">{name}</td>
                    <td className="py-1 pr-4 text-xs">
                      {t(SCOPE_LABELS[dataset.row_scope as keyof typeof SCOPE_LABELS] ?? dataset.row_scope)}
                    </td>
                    <td className="py-1 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(dataset.columns ?? {}).map(([column, action]) => {
                          const shown = columnAction(action);
                          return (
                            <Chip key={column} size="sm" variant="flat" color={shown.color} title={t(shown.label)}>
                              {column}
                            </Chip>
                          );
                        })}
                      </div>
                    </td>
                    <td className="py-1 font-mono text-xs text-default-500">
                      {(dataset.hidden_columns ?? []).join(', ') || '—'}
                    </td>
                  </tr>
                )];
                if (dataset.notes?.length) {
                  rows.push(
                    <tr key={`${name}-notes`}>
                      <td colSpan={4} className="pb-2 text-xs text-default-500">
                        <ul className="list-disc pl-5">
                          {dataset.notes.map((note) => <li key={note}>{note}</li>)}
                        </ul>
                      </td>
                    </tr>
                  );
                }
                return rows;
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1 text-xs text-default-500">
        {t('Column colours')}:
        {Object.entries(COLUMN_ACTIONS).filter(([key]) => key !== 'omit').map(([key, action]) => (
          <Chip key={key} size="sm" variant="flat" color={action.color}>{t(action.label)}</Chip>
        ))}
      </div>
      {result.rewritten_sql ? (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-foreground">{t('Rewritten SQL')}</span>
          <pre aria-label={t('Rewritten SQL')} className="overflow-x-auto whitespace-pre-wrap rounded-medium bg-default-100 p-3 font-mono text-xs">
            {result.rewritten_sql}
          </pre>
        </div>
      ) : null}
      {result.filters ? (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-foreground">{t('Filters')}</span>
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-medium bg-default-100 p-3 font-mono text-xs">
            {JSON.stringify(result.filters, null, 2)}
          </pre>
        </div>
      ) : null}
    </section>
  );
}

export default CheckAccessTab;
